import { useState, type CSSProperties } from "react";
import { Trans } from "react-i18next";
import { Shirt } from "lucide-react";

import { SCENE_STAGE } from "@/features/wash-start-guide/start-guide-parts";
import { TapHand } from "@/features/wash-start-guide/washer-scene";

// A Renzacci dryer, filmed by a "camera" like the washer scene:
// - "panel": camera zooms into the controller; the hand taps + and − and the
//   program (P1–P6) and temperature on the LCD follow
// - "door": camera back out; on a loop, clothes fly into the open drum and
//   the door swings shut with a gentle KATT (no shake: a dryer door closes easily)
// - "closed": door closed, drum still (loaded and waiting for the start)
// - "running": door closed, the drum spins up and keeps spinning
// The scene stays mounted between steps, so changing `view` animates the zoom.
// CSS 3D + SVG only (kiosk Chrome has no GPU / WebGL).

export type DryerView = "panel" | "door" | "closed" | "running";

const STAGE = SCENE_STAGE;
const MACHINE = { left: 250, top: 30, width: 460, height: 520 };

// controller panel, in machine coordinates; its SVG uses a 120×160 viewBox
const PANEL = { left: 30, top: 22, width: 120, height: 160 };
const ZOOM = 3.2;

const DOOR_CENTER = { x: 230, y: 345 };
const GASKET_R = 128;
const OPENING_R = 104;
const DOOR_R = 118;
const GLASS_R = 80;

const DOOR_LOOP_SECONDS = 6.5;
const PANEL_LOOP_SECONDS = 8;
const CAMERA_SECONDS = 1.4;
// opening shot: hold on the whole dryer this long, then zoom to the panel
const INTRO_HOLD_SECONDS = 0.8;
const CAMERA_EASE = "cubic-bezier(.65,0,.35,1)";
// Spins up (ease-in, so it reads as "starting") while the camera zooms out,
// then keeps turning at the speed the spin-up ended on (~1030°/s).
const DRUM_SPIN = `dryer-drum-spin 3s ease-in ${CAMERA_SECONDS / 2}s both, dryer-drum-steady 0.35s linear ${CAMERA_SECONDS / 2 + 3}s infinite`;

// The dryer's settings: P1 = 65 °C … P6 = 90 °C, each + / − moves one step.
// On the LCD while the hand taps + + − − (see dryer-temp-* keyframes).
const SETTINGS = [1, 2, 3, 2, 1].map((program) => ({
  program,
  temperature: 60 + program * 5,
}));

export function DryerScene({
  view,
  doorClosed = false,
  capacity,
}: {
  view: DryerView;
  // "panel" only: the door was closed in an earlier step (else it's open)
  doorClosed?: boolean;
  // kg, shown on the sticker like on the washer
  capacity: number;
}) {
  // only the first visit to the panel opens on the whole dryer; coming back
  // with the back button uses the normal camera move
  const [intro, setIntro] = useState(view === "panel");
  if (intro && view !== "panel") setIntro(false);
  return (
    <div
      // no backdrop: the dryer stands on the page, like the washer. Rounded,
      // so the zoomed-in panel's steel doesn't end in hard corners
      className="relative overflow-hidden rounded-[28px]"
      style={{ width: STAGE.width, height: STAGE.height }}
    >
      <DryerKeyframes />
      <div
        className="absolute left-0 top-0"
        style={{
          width: STAGE.width,
          height: STAGE.height,
          transformOrigin: "0 0",
          transform: cameraTransform(view),
          transition: `transform ${CAMERA_SECONDS}s ${CAMERA_EASE}`,
          animation: intro
            ? `dryer-camera-intro ${CAMERA_SECONDS}s ${CAMERA_EASE} ${INTRO_HOLD_SECONDS}s backwards`
            : "none",
        }}
      >
        {/* soft shadow so the machine stands on something */}
        <div
          // the stage ends just below the machine: a tighter shadow, so the
          // clip doesn't cut it off
          className="absolute rounded-full bg-black/25 blur-sm"
          style={{ left: MACHINE.left - 10, width: MACHINE.width + 20, top: MACHINE.top + MACHINE.height - 12, height: 18 }}
        />
        <Machine
          view={view}
          doorClosed={doorClosed}
          capacity={capacity}
          panelDelay={CAMERA_SECONDS + (intro ? INTRO_HOLD_SECONDS : 0)}
        />
      </div>
    </div>
  );
}

function cameraTransform(view: DryerView) {
  if (view !== "panel") return "translate(0px, 0px) scale(1)";
  const cx = MACHINE.left + PANEL.left + PANEL.width / 2;
  const cy = MACHINE.top + PANEL.top + PANEL.height / 2;
  const tx = STAGE.width / 2 - cx * ZOOM;
  const ty = STAGE.height / 2 - cy * ZOOM;
  return `translate(${tx}px, ${ty}px) scale(${ZOOM})`;
}

function Machine({
  view,
  doorClosed,
  capacity,
  panelDelay,
}: {
  view: DryerView;
  doorClosed: boolean;
  capacity: number;
  panelDelay: number;
}) {
  return (
    <div className="absolute" style={MACHINE}>
      {/* plinth */}
      <div className="absolute left-[10px] right-[10px] top-[486px] bottom-0 bg-gradient-to-b from-[#55595d] to-[#3c4044]" />
      {/* brushed steel body */}
      <div
        className="absolute left-0 right-0 top-0 h-[490px] rounded-[6px] border border-[#5e6266]"
        style={{
          background:
            "repeating-linear-gradient(90deg, rgba(255,255,255,0.035) 0 1px, transparent 1px 3px), linear-gradient(90deg, #74787c 0%, #a2a6a9 22%, #8d9195 50%, #a6aaad 78%, #73777b 100%)",
          perspective: "1100px",
        }}
      >
        {/* control band */}
        <div className="absolute inset-x-0 top-0 h-[204px] border-b-2 border-black/20 bg-white/10" />
        <div
          className="absolute"
          style={{ ...PANEL, filter: "drop-shadow(0 1px 1px rgba(0,0,0,.35))" }}
        >
          <ControllerPanel playing={view === "panel"} delay={panelDelay} />
        </div>
        <Sticker capacity={capacity} />
        <Gasket />
        <Drum spinning={view === "running"} />
        {view === "door" && <FlyingClothes />}
        <Door view={view} doorClosed={doorClosed} />
        {view === "door" && <Katt />}
      </div>
    </div>
  );
}

function Sticker({ capacity }: { capacity: number }) {
  return (
    <div className="absolute left-[222px] top-[30px] w-[210px] h-[96px] rounded-[10px] bg-gradient-to-r from-[#1f6f93] to-[#2a86ad] border border-[#185a77] flex items-center gap-3 px-3">
      <div className="size-[66px] shrink-0 rounded-full border-[5px] border-white/90 bg-[#1b5f80] flex items-center justify-center text-white text-[16px] font-bold">
        {capacity}kg
      </div>
      <div className="flex-1 flex flex-col gap-[6px]">
        <span className="text-[11px] font-black tracking-[0.15em] text-white">
          RENZACCI
        </span>
        {[90, 64, 80].map((width, index) => (
          <span
            key={index}
            className="h-[4px] rounded bg-white/70"
            style={{ width: `${width}%` }}
          />
        ))}
      </div>
    </div>
  );
}

// ---- controller (drawn from a photo of our dryers) ----

function ControllerPanel({
  playing,
  delay,
}: {
  playing: boolean;
  delay: number;
}) {
  // wait for the camera to arrive before the demo starts
  const demo = (name: string): CSSProperties => ({
    animation: playing
      ? `${name} ${PANEL_LOOP_SECONDS}s ${delay}s infinite backwards`
      : "none",
    transformBox: "fill-box",
    transformOrigin: "center",
  });
  return (
    <div className="relative size-full">
      <svg viewBox="0 0 120 160" className="absolute inset-0 size-full">
        <defs>
          <linearGradient id="dryer-panel" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#8e0c13" />
            <stop offset="0.3" stopColor="#c8161d" />
            <stop offset="0.46" stopColor="#ecebea" />
            <stop offset="0.56" stopColor="#d2d2d0" />
            <stop offset="0.74" stopColor="#b3121a" />
            <stop offset="1" stopColor="#6e080d" />
          </linearGradient>
          <linearGradient id="dryer-lcd" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#3aa8ec" />
            <stop offset="1" stopColor="#8a72e2" />
          </linearGradient>
          <radialGradient id="dryer-key" cx="0.5" cy="0.4" r="0.6">
            <stop offset="0" stopColor="#ffd25a" />
            <stop offset="0.6" stopColor="#f39a1c" />
            <stop offset="1" stopColor="#d4600e" />
          </radialGradient>
        </defs>

        <rect width="120" height="160" rx="5" fill="url(#dryer-panel)" />

        {/* LCD */}
        <rect x="11" y="23" width="86" height="35" rx="1" fill="#3f6c70" />
        <rect x="14" y="28" width="80" height="24" fill="#111" />
        <rect x="16" y="30" width="76" height="20" fill="url(#dryer-lcd)" />
        <g
          fill="#f7e86a"
          fontFamily="ui-monospace, Menlo, monospace"
          fontWeight="700"
          fontSize="6.4"
        >
          <text x="23" y="46.5">
            01'
          </text>
          {SETTINGS.map(({ program, temperature }, index) => (
            <g
              key={index}
              opacity={index === 0 ? 1 : 0}
              style={demo(`dryer-temp-${index}`)}
            >
              <text x="20" y="38">
                P{program}
              </text>
              <text x="40" y="46.5">
                {temperature}°C
              </text>
            </g>
          ))}
          <text x="62" y="46.5">
            40
          </text>
        </g>
        {/* marks the temperature while we demo it */}
        {playing && (
          <rect
            x="38.5"
            y="40"
            width="21"
            height="8.5"
            rx="1.5"
            fill="none"
            stroke="#f45e22"
            strokeWidth="1.2"
            style={demo("dryer-temp-mark")}
          />
        )}

        {/* black key field */}
        <rect x="12" y="72" width="83" height="41" fill="#050505" />
        <Shirts />
        <text
          x="88"
          y="98"
          fontSize="7"
          textAnchor="middle"
          fill="#777"
          fontFamily="sans-serif"
        >
          ⚙
        </text>

        {/* + key: lens pointing up */}
        <g style={demo("dryer-key-plus")}>
          <path d="M54 66 C60 70 62 76 62 83 C58 81 50 81 46 83 C46 76 48 70 54 66Z" fill="url(#dryer-key)" />
          <path d="M51 75 h6 M54 72 v6" stroke="#1d3a8a" strokeWidth="0.9" />
        </g>
        {/* start / stop (not for customers) */}
        <circle cx="54" cy="92" r="9.5" fill="#050505" />
        <circle cx="54" cy="92" r="7.8" fill="url(#dryer-key)" />
        <path d="M46.5 92 h15" stroke="#1a1a1a" strokeWidth="0.4" />
        <g fontSize="3.3" textAnchor="middle" fill="#1a1a1a" fontWeight="700" fontFamily="sans-serif">
          <text x="54" y="90.6">Start</text>
          <text x="54" y="95.6">Stop</text>
        </g>
        {/* − key: lens pointing down */}
        <g style={demo("dryer-key-minus")}>
          <path d="M54 118 C48 114 46 108 46 101 C50 103 58 103 62 101 C62 108 60 114 54 118Z" fill="url(#dryer-key)" />
          <path d="M51 110 h6" stroke="#1d3a8a" strokeWidth="0.9" />
        </g>
        {/* edit (service) */}
        <circle cx="83" cy="109" r="6" fill="url(#dryer-key)" />
        <text x="83" y="110.4" fontSize="3.4" textAnchor="middle" fill="#1a1a1a" fontWeight="700" fontFamily="sans-serif">
          Edit
        </text>

        <text
          x="60"
          y="130"
          fontSize="4"
          textAnchor="middle"
          fill="#fff"
          fontStyle="italic"
          fontWeight="700"
          fontFamily="sans-serif"
        >
          Microprocessor Controlled System
        </text>
      </svg>

      {/* fingertip sits on the + key, the − key is 34 units lower */}
      {playing && (
        <span
          className="absolute size-0"
          style={{
            left: `${(54 / 120) * 100}%`,
            top: `${(77 / 160) * 100}%`,
            ...demo("dryer-hand"),
            transformBox: "border-box",
            transformOrigin: "0 0",
          }}
        >
          <TapHand className="absolute -left-[10.5px] -top-[3px] size-[28px] drop-shadow" />
        </span>
      )}
    </div>
  );
}

function Shirts() {
  return (
    <g fill="#2a2a2a">
      <path d="M20 84 l4 -3 h4 l4 3 l-2 3 l-2 -1 v7 h-8 v-7 l-2 1Z" />
      <path d="M26 92 l4 -3 h4 l4 3 l-2 3 l-2 -1 v7 h-8 v-7 l-2 1Z" />
    </g>
  );
}

// ---- door & drum ----

function Gasket() {
  return (
    <div
      className="absolute rounded-full"
      style={{
        ...circle(DOOR_CENTER, GASKET_R),
        background: "radial-gradient(circle, #9aa0a5 60%, #5c6166 100%)",
        boxShadow: "inset 0 0 0 3px #45494d, 0 2px 0 rgba(255,255,255,0.15)",
      }}
    />
  );
}

// stainless, perforated dryer drum
function Drum({ spinning }: { spinning: boolean }) {
  return (
    <div
      className="absolute rounded-full overflow-hidden"
      style={{
        ...circle(DOOR_CENTER, OPENING_R),
        animation: spinning ? DRUM_SPIN : "none",
        background:
          "radial-gradient(circle, rgba(0,0,0,0.35) 1.5px, transparent 2.5px) 0 0 / 11px 11px, radial-gradient(circle at 50% 40%, #c9ced2, #6d7378 80%)",
        boxShadow: "inset 0 12px 34px rgba(0,0,0,0.6)",
      }}
    >
      <Shirt className="absolute size-24 left-[6%] bottom-[4%] rotate-[-20deg] text-white fill-bub-vivid-blue" />
      <Shirt className="absolute size-20 right-[4%] bottom-[8%] rotate-[25deg] text-white fill-bub-orange" />
    </div>
  );
}

// thrown into the drum while the door is open (start of the door loop)
function FlyingClothes() {
  return (
    <>
      {[
        { color: "fill-[#f4c542]", keyframes: "dryer-throw-1" },
        { color: "fill-bub-vivid-blue", keyframes: "dryer-throw-2" },
      ].map(({ color, keyframes }) => (
        <Shirt
          key={keyframes}
          className={`absolute size-24 text-white ${color} drop-shadow-lg`}
          style={{
            left: DOOR_CENTER.x - 48,
            top: DOOR_CENTER.y - 48,
            animation: loop(keyframes, DOOR_LOOP_SECONDS),
          }}
        />
      ))}
    </>
  );
}

const ringMask = {
  maskImage: `radial-gradient(circle, transparent ${GLASS_R - 1}px, black ${GLASS_R}px)`,
  WebkitMaskImage: `radial-gradient(circle, transparent ${GLASS_R - 1}px, black ${GLASS_R}px)`,
} satisfies CSSProperties;

// Hinged on the right, like our machines. Open (rotateY(105deg)) while
// setting the temperature first, closed if it was shut in an earlier step;
// swings shut on a loop in the "door" view.
function Door({ view, doorClosed }: { view: DryerView; doorClosed: boolean }) {
  const closed =
    view === "running" || view === "closed" || (view === "panel" && doorClosed);
  return (
    <div
      className="absolute"
      style={{
        ...circle(DOOR_CENTER, DOOR_R),
        transformStyle: "preserve-3d",
        transformOrigin: "100% 50%",
        transform: closed ? "none" : "rotateY(105deg)",
        animation:
          view === "door" ? loop("dryer-door", DOOR_LOOP_SECONDS) : "none",
      }}
    >
      {/* glossy red ring, like on our dryers */}
      <DoorLayer z={0} style={{ background: "#5a0a10", ...ringMask }} />
      <DoorLayer z={7} style={{ background: "#8f1219", ...ringMask }} />
      <DoorLayer
        z={14}
        style={{
          background:
            "conic-gradient(from 210deg, #ff6b6b, #b3141d, #f23b3b, #9c1018, #e83030, #c2161f, #ff6b6b)",
          ...ringMask,
        }}
      />
      <div
        className="absolute rounded-full"
        style={{
          inset: DOOR_R - GLASS_R,
          transform: "translateZ(12px)",
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0.05) 36%, rgba(60,70,80,0.18) 62%, rgba(255,255,255,0.22) 100%)",
          boxShadow: "inset 0 0 0 6px rgba(20,22,24,0.85)",
        }}
      />
      {/* the black handle on the ring's left edge, opposite the hinge */}
      <div
        className="absolute rounded-[5px]"
        style={{
          left: -4,
          top: DOOR_R - 19,
          width: 32,
          height: 38,
          transform: "translateZ(16px)",
          background: "linear-gradient(90deg, #2b2b2b, #0d0d0d 60%, #1c1c1c)",
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.18), 0 3px 6px rgba(0,0,0,0.45)",
        }}
      />
    </div>
  );
}

function DoorLayer({ z, style }: { z: number; style: CSSProperties }) {
  return (
    <div
      className="absolute inset-0 rounded-full"
      style={{ transform: `translateZ(${z}px)`, ...style }}
    />
  );
}

function Katt() {
  return (
    <>
      <div
        className="absolute rounded-full border-[6px] border-white pointer-events-none"
        style={{
          ...circle(DOOR_CENTER, GASKET_R),
          animation: loop("dryer-ripple", DOOR_LOOP_SECONDS),
        }}
      />
      <span
        className="absolute right-[340px] top-[230px] rounded-xl bg-bub-orange px-4 py-2.5 text-white font-fancy font-bold text-[40px] leading-none whitespace-nowrap shadow-lg pointer-events-none"
        style={{ animation: loop("dryer-katt", DOOR_LOOP_SECONDS) }}
      >
        <Trans>KATT!</Trans>
      </span>
    </>
  );
}

// ---- helpers ----

function circle(center: { x: number; y: number }, radius: number) {
  return {
    left: center.x - radius,
    top: center.y - radius,
    width: radius * 2,
    height: radius * 2,
  };
}

function loop(name: string, seconds: number) {
  return `${name} ${seconds}s infinite`;
}

// Door loop (6.5 s): two shirts go in 0–34%, door shuts 35–55% and settles
// softly, KATT 55–61%, stays closed, opens again 90–100%.
// Panel loop (8 s): taps on + at 15% and 35% (P1 → P2 → P3), on − at 55% and
// 75% (back to P1). The hand moves down to − during 40–48% and back up during
// 86–94%.
// Each temperature text is visible during its own window.
function DryerKeyframes() {
  return (
    <style>{`
      @keyframes dryer-camera-intro {
        from { transform: translate(0px, 0px) scale(1); }
        to { transform: ${cameraTransform("panel")}; }
      }
      @keyframes dryer-drum-spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(1800deg); }
      }
      @keyframes dryer-drum-steady {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes dryer-temp-0 { 0%, 14.9% { opacity: 1; } 15%, 100% { opacity: 0; } }
      @keyframes dryer-temp-1 { 0%, 14.9% { opacity: 0; } 15%, 34.9% { opacity: 1; } 35%, 100% { opacity: 0; } }
      @keyframes dryer-temp-2 { 0%, 34.9% { opacity: 0; } 35%, 54.9% { opacity: 1; } 55%, 100% { opacity: 0; } }
      @keyframes dryer-temp-3 { 0%, 54.9% { opacity: 0; } 55%, 74.9% { opacity: 1; } 75%, 100% { opacity: 0; } }
      @keyframes dryer-temp-4 { 0%, 74.9% { opacity: 0; } 75%, 100% { opacity: 1; } }
      @keyframes dryer-hand {
        0%, 13%, 17%, 33%, 37%, 40% { transform: translateY(0) scale(1); }
        15%, 35% { transform: translateY(0) scale(.85); }
        48%, 53% { transform: translateY(34px) scale(1); }
        55% { transform: translateY(34px) scale(.85); }
        57%, 73% { transform: translateY(34px) scale(1); }
        75% { transform: translateY(34px) scale(.85); }
        77%, 86% { transform: translateY(34px) scale(1); }
        94%, 100% { transform: translateY(0) scale(1); }
      }
      @keyframes dryer-key-plus {
        0%, 14%, 17%, 34%, 37%, 100% { filter: none; transform: none; }
        15%, 35% { filter: brightness(1.35); transform: scale(.94); }
      }
      @keyframes dryer-key-minus {
        0%, 54%, 57%, 74%, 77%, 100% { filter: none; transform: none; }
        55%, 75% { filter: brightness(1.35); transform: scale(.94); }
      }
      @keyframes dryer-temp-mark {
        0%, 100% { opacity: .35; }
        50% { opacity: 1; }
      }
      @keyframes dryer-throw-1 {
        0% { opacity: 0; transform: translate(-260px, -260px) rotate(40deg) scale(1.3); animation-timing-function: ease-in; }
        4% { opacity: 1; }
        22% { opacity: 1; transform: translate(0, 0) rotate(-20deg) scale(.7); }
        27%, 100% { opacity: 0; transform: translate(0, 20px) rotate(-30deg) scale(.5); }
      }
      @keyframes dryer-throw-2 {
        0%, 9% { opacity: 0; transform: translate(-260px, -260px) rotate(40deg) scale(1.3); animation-timing-function: ease-in; }
        13% { opacity: 1; }
        30% { opacity: 1; transform: translate(0, 0) rotate(-20deg) scale(.7); }
        34%, 100% { opacity: 0; transform: translate(0, 20px) rotate(-30deg) scale(.5); }
      }
      @keyframes dryer-door {
        0%, 35% { transform: rotateY(105deg); animation-timing-function: cubic-bezier(.33,0,.25,1); }
        55%, 90% { transform: rotateY(0deg); animation-timing-function: ease-in-out; }
        100% { transform: rotateY(105deg); }
      }
      @keyframes dryer-ripple {
        0%, 55% { opacity: 0; transform: scale(1); }
        56% { opacity: .5; transform: scale(1); }
        66%, 100% { opacity: 0; transform: scale(1.15); }
      }
      @keyframes dryer-katt {
        0%, 55% { opacity: 0; transform: rotate(-12deg) scale(.3); }
        58% { opacity: 1; transform: rotate(-12deg) scale(1.15); }
        61%, 84% { opacity: 1; transform: rotate(-12deg) scale(1); }
        88%, 100% { opacity: 0; transform: rotate(-12deg) scale(1); }
      }
    `}</style>
  );
}
